import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import cloneDeep from 'lodash/cloneDeep';
import styleable from 'react-styleable';
import styles from './index.scss';
import Button from '../../components/button/index';
import { CIRCLE_DANGER, CIRCLE_SUCCESS } from '../../components/button/variants';
import withForm from '../../components/with-form';
import DropDownList from '../dropdown-list/index';
import { Cross, Tick, Edit } from '../../helpers/icon-dictionary';
import messages from './messages';
import classEnum from '../../containers/class-roll/constants';
import withModal from '../with-modal';
import productEnum from '../../helpers/product-enum';
import RadioButton from '../radiobutton';
import { DEFAULT_COUNTRY_ID, DEFAULT_LEGACY_COURSE_ID, DEFAULT_NEW_COURSE_ID, DEFAULT_UPF_COURSE_ID, DEFAULT_YEAR_GROUP } from '../../constants';
import { findByKey } from './helpers';
import { getNewCoursesTitle, newCoursesAvailable } from '../../helpers/utils';

class ChangeCourseForm extends Component {
    constructor(props) {
        super(props);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleListChange = this.handleListChange.bind(this);
        this.handleOnSelect = this.handleOnSelect.bind(this);
        this.getDisplayNewCourses = this.getDisplayNewCourses.bind(this);
        const { intl, selectedProduct } = this.props;
        const isMathletics = (selectedProduct.id === productEnum.MATHLETICS.id);
        let lists;

        if (isMathletics) {
            lists = [{
                key: 'country',
                pluralKey: 'countries',
                title: intl.formatMessage(messages.selectCountry),
                shortTitle: intl.formatMessage(messages.countryLabel),
                placeholder: intl.formatMessage(messages.selectCountry),
                next: 'yearGroup',
                clear: ['country', 'yearGroup', 'course', 'upfCourse', 'newCourses'],
                isActive: true
            },
            {
                key: 'yearGroup',
                pluralKey: 'yearGroups',
                title: intl.formatMessage(messages.selectGrade),
                shortTitle: intl.formatMessage(messages.gradeLabel),
                placeholder: intl.formatMessage(messages.selectGrade),
                next: 'course',
                clear: ['yearGroup', 'course', 'upfCourse', 'newCourse'],
                isActive: false
            },
            {
                key: 'course',
                pluralKey: 'courses',
                title: intl.formatMessage(messages.selectCourse),
                shortTitle: intl.formatMessage(messages.courseLabel),
                placeholder: intl.formatMessage(messages.selectCourse),
                emptyPlaceholder: intl.formatMessage(messages.noCoursesFound),
                next: 'upfCourse',
                clear: ['course', 'newCourses', 'upfCourse'],
                isActive: false
            },
            {
                key: 'upfCourse',
                pluralKey: 'upfCourses',
                title: intl.formatMessage(messages.selectSkillQuests),
                shortTitle: intl.formatMessage(messages.skillQuests),
                placeholder: intl.formatMessage(messages.selectSkillQuests),
                next: props.displayNewCourses ? 'newCourses' : undefined,
                emptyPlaceholder: intl.formatMessage(messages.noCoursesFound),
                clear: ['newCourses', 'upfCourse'],
                isActive: false
            }];

            if (props.displayNewCourses) {
                const IS_NEW_COURSES_ASSIGNED = false;
                lists.push({
                    key: 'newCourses',
                    pluralKey: 'newCourses',
                    title: intl.formatMessage(messages.selectNewCourse),
                    shortTitle: intl.formatMessage(messages.newCourses),
                    placeholder: intl.formatMessage(messages.selectNewCourse),
                    emptyPlaceholder: getNewCoursesTitle(this.props.form.yearGroup, IS_NEW_COURSES_ASSIGNED, '', intl),
                    clear: ['newCourses'],
                    isActive: false
                });
            }
        } else {
            lists = [
                {
                    key: 'country',
                    pluralKey: 'countries',
                    title: intl.formatMessage(messages.selectCountry),
                    shortTitle: intl.formatMessage(messages.countryLabel),
                    placeholder: intl.formatMessage(messages.selectCountry),
                    next: 'yearGroup',
                    clear: ['country', 'yearGroup', 'course'],
                    isActive: true
                },
                {
                    key: 'yearGroup',
                    pluralKey: 'yearGroups',
                    title: intl.formatMessage(messages.selectGrade),
                    shortTitle: intl.formatMessage(messages.gradeLabel),
                    placeholder: intl.formatMessage(messages.selectGrade),
                    next: 'course',
                    clear: ['yearGroup', 'course'],
                    isActive: false
                },
                {
                    key: 'course',
                    pluralKey: 'courses',
                    title: intl.formatMessage(messages.selectCourse),
                    shortTitle: intl.formatMessage(messages.courseLabel),
                    placeholder: intl.formatMessage(messages.selectCourse),
                    emptyPlaceholder: intl.formatMessage(messages.noCoursesFound),
                    clear: ['course'],
                    isActive: false
                }
            ];
        }

        this.state = {
            lists,
            fetchCourse: false,
            selectorPressed: false,
            isSchoolCoursesSelected: false,
            isCurriculumCoursesSelected: true,
            selectedLegacyCourse: DEFAULT_LEGACY_COURSE_ID,
            selectedCountry: DEFAULT_COUNTRY_ID,
            selectedGrade: DEFAULT_YEAR_GROUP
        };
    }

    componentDidMount() {
        const { countries, getCourseCountries, selectedProduct } = this.props;
        if (!countries.length) {
            getCourseCountries(selectedProduct.id);
        }
    }

    getDisplayNewCourses = () => this.props.displayNewCourses;

    static displayNewCourses = this && this.getDisplayNewCourses();

    handleOnSchoolCoursesSelection = () => {
        this.setState({
            isSchoolCoursesSelected: true,
            isCurriculumCoursesSelected: false,
            selectedLegacyCourse: DEFAULT_LEGACY_COURSE_ID
        });

        this.props.clearProductCourses();
        this.props.getSchoolCourses(this.state.selectedGrade);
    }

    handleOnCurriculumCoursesSelection = () => {
        const { clearProductCourses, getProductCourses, selectedProduct } = this.props;
        this.setState({
            isSchoolCoursesSelected: false,
            isCurriculumCoursesSelected: true,
            selectedLegacyCourse: DEFAULT_LEGACY_COURSE_ID
        });

        clearProductCourses();
        getProductCourses(this.state.selectedCountry, selectedProduct.id, this.state.selectedGrade);
    }

    handleListChange(field, value, form) {
        const { yearGroup, country } = form;
        const {
            getSchoolCourses,
            selectedProduct: {
                id: selectedProductId
            },
            getUPFCourses,
            getNewCourses,
            getProductCourses,
            handleCountryDropdownChanges
        } = this.props;

        this.setState({
            selectedCountry: country,
            selectedGrade: yearGroup
        });

        if (field === 'country') {
            handleCountryDropdownChanges(field, value);
        }

        if (
            yearGroup !== undefined &&
            yearGroup !== DEFAULT_YEAR_GROUP &&
            country !== undefined &&
            country !== DEFAULT_COUNTRY_ID
        ) {
            if (selectedProductId === productEnum.MATHLETICS.id) {
                if (field === 'upfCourse') getNewCourses(selectedProductId);

                if (field === 'course') getUPFCourses(country, selectedProductId, yearGroup);
            }

            if (country === classEnum.SCHOOL_COURSES || this.state.isSchoolCoursesSelected) {
                getSchoolCourses(yearGroup);
            } else if (field === 'yearGroup') {
                getProductCourses(country, selectedProductId, yearGroup);
            }
        }

        // Set the next dropdown list
        const currentList = findByKey(field, this.state.lists);

        if (currentList.next) {
            const nextList = findByKey(currentList.next, this.state.lists);
            this.handleOnSelect(nextList);
        } else {
            currentList.hideOverride = true;
            currentList.isActive = false;
            this.handleOnSelect(currentList);
        }
    }

    handleClose() {
        this.props.closeChangeCourse();
    }

    handleFormSubmit() {
        const { classroomId, groupId, fields, changeCourse } = this.props;
        const { course, upfCourse, newCourses } = fields;

        changeCourse({
            classroomId,
            groupId,
            courseId: course,
            submittedFields: fields,
            upfCourseId: upfCourse,
            newCourseId: (!newCourses || newCourses === '-1') ? DEFAULT_NEW_COURSE_ID : newCourses
        });
    }

    handleOnSelect(list) {
        const { fields } = this.props;
        const lists = cloneDeep(this.state.lists);
        const updatedLists = lists.map(item => ({
            ...item,
            isActive: list.key === item.key
        }));

        // Clear associated fields
        list.clear.forEach((key) => {
            fields[key] = '-1';
        });

        this.setState({
            lists: updatedLists,
            selectorPressed: list.next !== undefined || list.isActive
        });
    }

    courseSelectors = () => {
        const { intl } = this.props;
        return (
            <div className={styles.courseSelectorWrapper} id="courseSelector">
                <div className={styles.customCoursesWrapper}>
                    <RadioButton
                        checked={this.state.isSchoolCoursesSelected}
                        id="customCourses"
                        name="courseSelector"
                        onChange={this.handleOnSchoolCoursesSelection}
                        value={intl.formatMessage(messages.customCourses)} />
                    <label htmlFor="customCourses">{intl.formatMessage(messages.customCourses)}</label>
                </div>
                <div className={styles.curriculumCoursesWrapper}>
                    <RadioButton
                        checked={this.state.isCurriculumCoursesSelected}
                        id="curriculumCourses"
                        name="courseSelector"
                        onChange={this.handleOnCurriculumCoursesSelection}
                        value={intl.formatMessage(messages.customCourses)} />
                    <label htmlFor="curriculumCourses">{intl.formatMessage(messages.curriculumCourses)}</label>
                </div>
            </div>
        );
    }

    render() {
        const {
            canSubmit,
            classroomDetail,
            form,
            fields,
            groupId,
            intl,
            isChangingCourseInProgress,
            modal,
            selectedProduct
        } = this.props;


        const formTitle = this.state.lists.map((item) => {
            const fieldValue = fields[item.key];
            const titleStyle = item.isActive ? '' : styles.hidden;
            const title = (!item.next && (fieldValue !== undefined && fieldValue !== '-1')) ? intl.formatMessage(messages.confirmLabel) : item.title;
            return item.isActive ? (<div className={titleStyle} key={item.key}>{title}</div>) : '';
        });

        const buttonSelectors = this.state.lists.map((item) => {
            // don't show the UPF course selector option if product is non Mathletics
            if (selectedProduct.id !== productEnum.MATHLETICS.id && item.key === 'upfCourse') {
                return null;
            }

            const currentValue = fields[item.key];
            const selectedOption = findByKey(currentValue, this.props[item.pluralKey]);
            const buttonLabel = selectedOption ? selectedOption.value : item.shortTitle;
            const isClickable = currentValue && currentValue !== '-1';
            const isClickableClass = isClickable ? styles.actionClickable : styles.actionUnclickable;
            return (
                <Button
                    buttonContainer={`${styles.buttonContainer}`}
                    containerClassName={`${styles.buttonWrapper} ${styles.buttonSelector} ${isClickableClass}`}
                    handleOnClick={() => { if (isClickable) this.handleOnSelect(item); }}
                    icon={Edit}
                    iconClassName={styles.buttonIcon}
                    item={item}
                    key={item.key}
                    label={buttonLabel}
                />);
        });

        const dropdowns = this.state.lists.map((item) => {
            const fieldValue = fields[item.key];
            const isDropdownLoading = this.props[`${item.key}Fetching`];
            let optionLists = this.props[item.pluralKey] || [];
            const isMathletics = (selectedProduct.id === productEnum.MATHLETICS.id);

            if ((!this.state.isCurriculumCoursesSelected && !this.state.isSchoolCoursesSelected) &&
            item.key === 'course' && item.isActive) {
                optionLists = [];
            }

            const areNewCoursesAvailable = newCoursesAvailable(this.state.selectedGrade, this.state.selectedCountry);
            if (item.key === 'newCourses' && !areNewCoursesAvailable) {
                optionLists = [];
            }

            if (!isMathletics && item.key === 'upfCourse') {
                return false;
            }

            const dropdownProps = {
                course: {
                    disabled: optionLists.length === 0 && !isDropdownLoading,
                    placeholder: optionLists.length === 0 && !isDropdownLoading ? item.emptyPlaceholder : item.placeholder
                },
                newCourses: {
                    optionKey: 'key',
                    optionText: 'name',
                    disabled: optionLists.length === 0 && !isDropdownLoading,
                    placeholder: optionLists.length === 0 && !isDropdownLoading ? item.emptyPlaceholder : item.placeholder
                },
                upfCourse: {
                    optionKey: 'key',
                    optionText: 'name',
                    disabled: optionLists.length === 0 && !isDropdownLoading,
                    placeholder: optionLists.length === 0 && !isDropdownLoading ? item.emptyPlaceholder : item.placeholder
                }
            };
            let dropdownStyle = item.isActive ? '' : styles.hidden;
            if (!item.next && (fieldValue !== undefined && fieldValue !== '-1')) {
                dropdownStyle = styles.hidden;
            }

            return (
                <div key={item.key}>
                    {isMathletics && (fieldValue === undefined || fieldValue === '-1')
                    && item.key === 'course' && item.isActive && this.courseSelectors()}
                    <div className={`${dropdownStyle}`}>
                        <DropDownList
                            isLoading={isDropdownLoading}
                            name={item.key}
                            onChange={(field, value) => { form.handleListOnChange(field, value, this.handleListChange); }}
                            options={optionLists}
                            placeholder={item.placeholder}
                            value={fieldValue}
                            {...dropdownProps[item.key]}
                        />
                    </div>
                </div>);
        });

        const { classroomGroups, name: classroomName } = classroomDetail;
        const currentGroup = classroomGroups.find(group => group.id === groupId);
        const isMathletics = (selectedProduct.id === productEnum.MATHLETICS.id);
        const { courseTitle, upfCourseTitle, conquestCourseTitle } = currentGroup;

        const { selectorPressed } = this.state;

        const formContent = (
            <div className={styles.changeCourseContainer}>
                <div className={styles.title}>
                    {formTitle}
                    <div className={styles.titleSub}>
                        <span className={styles.course}>{classroomName}</span>, <span className={styles.course}>{currentGroup.name}</span> {intl.formatMessage(messages.subTitle)} <span className={styles.course}>{courseTitle}</span>
                        <span>{isMathletics && upfCourseTitle && <span>, <span className={styles.course}>{upfCourseTitle}</span></span>}</span>
                        <span>{isMathletics && conquestCourseTitle && <span> & <span className={styles.course}>{conquestCourseTitle}</span></span>}</span>
                    </div>
                </div>
                <div className={styles.buttonSelectors}>
                    {buttonSelectors}
                </div>
                {dropdowns}
                <div className={styles.actionButtonWrapper}>
                    <Button
                        containerClassName={`cancel-button ${styles.btnDanger}`}
                        disabled={isChangingCourseInProgress}
                        handleOnClick={() => { modal.close(this.handleClose); }}
                        icon={Cross}
                        iconClassName={styles.svgIconContainer}
                        variant={CIRCLE_DANGER}
                    />
                    <Button
                        containerClassName={`ok-button ${styles.btnSuccess}`}
                        disabled={!canSubmit || selectorPressed || isChangingCourseInProgress}
                        handleOnClick={() => { form.submit(this.handleFormSubmit); }}
                        icon={Tick}
                        iconClassName={styles.svgIconContainer}
                        variant={CIRCLE_SUCCESS}
                    />
                </div>
            </div>
        );


        return (
            formContent
        );
    }
}

ChangeCourseForm.defaultProps = {
    fields: {
        upfCourse: DEFAULT_UPF_COURSE_ID,
        newCourse: DEFAULT_NEW_COURSE_ID
    }
};

ChangeCourseForm.propTypes = {
    canSubmit: PropTypes.bool,
    classroomDetail: PropTypes.object,
    classroomId: PropTypes.number,
    closeChangeCourse: PropTypes.func,
    changeCourse: PropTypes.func,
    fields: PropTypes.object,
    form: PropTypes.object,
    getProductCourses: PropTypes.func,
    getSchoolCourses: PropTypes.func,
    groupId: PropTypes.number,
    handleCountryDropdownChanges: PropTypes.func,
    intl: intlShape.isRequired,
    isChangingCourseInProgress: PropTypes.bool,
    modal: PropTypes.object,
    selectedProduct: PropTypes.object,
    getUPFCourses: PropTypes.func.isRequired,
    clearProductCourses: PropTypes.func,
    countries: PropTypes.array,
    getCourseCountries: PropTypes.func,
    getNewCourses: PropTypes.func,
    displayNewCourses: PropTypes.bool
};

const StyledChangeCourse = injectIntl(
    styleable(styles)(ChangeCourseForm)
);

let ChangeCourseWithForm = withForm(StyledChangeCourse, { validate: fields => fields.upfCourse && fields.upfCourse !== '-1' });

if (ChangeCourseForm.displayNewCourses) {
    ChangeCourseWithForm = withForm(StyledChangeCourse, { validate: fields => fields.newCourse && fields.newCourse !== '-1' });
}

export default withModal('isActive')(ChangeCourseWithForm);



// WEBPACK FOOTER //
// ./app/components/change-course/form.js